import React from 'react';
import { GENRE_LIBRARY } from '../data/GenreLibrary';
import Knob from './Knob';
import TempoInput from './TempoInput';

// This component consolidates Genre, Key, Scale, and Tempo into one top bar.
const GlobalControlBar = ({
    currentEnsemble,
    currentSubGenre,
    onGenreChange,
    rootKey,
    setRootKey,
    scaleType,
    setScaleType,
    tempo,
    setTempo,
    // New Complexity Vectors
    rhythmicDensity,
    setRhythmicDensity,
    harmonicFreedom,
    setHarmonicFreedom,
    melodicRange,
    setMelodicRange,
    timbralMotion,
    setTimbralMotion
}) => {

    // Genre List (Ensemble Level)
    const ensembles = Object.keys(GENRE_LIBRARY).map(key => ({
        key: key,
        name: GENRE_LIBRARY[key].name
    }));

    const handleEnsembleClick = (ensembleKey) => {
        // If clicking the current one again, or switching, ensure we trigger the load logic.
        // If we switch ensemble, we pick the first subgenre.
        const firstSub = Object.keys(GENRE_LIBRARY[ensembleKey].subGenres)[0];

        // IMPORTANT: We must explicitly pass the new ensembleKey to update App state AND trigger preset load.
        onGenreChange(firstSub, ensembleKey);
    };

    // Sub-Genre List (Dependent on Ensemble)
    const subGenres = (GENRE_LIBRARY[currentEnsemble] && GENRE_LIBRARY[currentEnsemble].subGenres)
        ? Object.keys(GENRE_LIBRARY[currentEnsemble].subGenres).map(key => ({
            key: key,
            name: GENRE_LIBRARY[currentEnsemble].subGenres[key].name
        }))
        : [];

    return (
        <div className="global-control-bar" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            padding: '10px 20px',
            background: '#111',
            borderBottom: '1px solid #333',
            flexWrap: 'wrap'
        }}>
            {/* 1. Ensemble Selector (Tabs) */}
            <div style={{ display: 'flex', gap: '5px' }}>
                {ensembles.map(e => (
                    <button
                        key={e.key}
                        onClick={() => handleEnsembleClick(e.key)}
                        style={{
                            background: currentEnsemble === e.key ? 'var(--color-primary)' : 'transparent',
                            color: currentEnsemble === e.key ? '#000' : '#888',
                            border: '1px solid #444',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {e.name}
                    </button>
                ))}
            </div>

            <div style={{ width: '1px', height: '30px', background: '#333' }}></div>

            {/* 2. Sub-Genre Selector (Dropdown-ish Grid) */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '0.7rem', color: '#666', marginBottom: '2px' }}>SUB-STYLE</span>
                <select
                    value={currentSubGenre}
                    onChange={(e) => onGenreChange(e.target.value, currentEnsemble)}
                    style={{
                        background: '#222',
                        color: 'var(--text-main)',
                        border: '1px solid #444',
                        padding: '4px',
                        borderRadius: '4px',
                        width: '150px'
                    }}
                >
                    {subGenres.map(s => (
                        <option key={s.key} value={s.key}>{s.name}</option>
                    ))}
                </select>
            </div>

            <div style={{ width: '1px', height: '30px', background: '#333' }}></div>

            {/* 3. Key & Scale */}
            <div style={{ display: 'flex', gap: '10px' }}>
                 <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.7rem', color: '#666', marginBottom: '2px' }}>KEY</span>
                    <select
                        value={rootKey}
                        onChange={(e) => setRootKey(e.target.value)}
                        style={{ background: '#222', color: 'var(--text-main)', border: '1px solid #444', borderRadius: '4px', padding: '4px' }}
                    >
                        {['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'].map(k => (
                            <option key={k} value={k}>{k}</option>
                        ))}
                    </select>
                 </div>
                 <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.7rem', color: '#666', marginBottom: '2px' }}>SCALE</span>
                    <select
                        value={scaleType}
                        onChange={(e) => setScaleType(e.target.value)}
                        style={{ background: '#222', color: 'var(--text-main)', border: '1px solid #444', borderRadius: '4px', padding: '4px' }}
                    >
                        {['major','minor','dorian','phrygian','lydian','mixolydian','locrian','pentatonic_major','pentatonic_minor','blues','harmonic_minor','melodic_minor'].map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                 </div>
            </div>

            <div style={{ width: '1px', height: '30px', background: '#333' }}></div>

            {/* 4. Tempo */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '0.7rem', color: '#666', marginBottom: '2px' }}>TEMPO</span>
                <TempoInput
                    value={tempo}
                    onChange={setTempo}
                    min={40}
                    max={220}
                />
            </div>

            <div style={{ width: '1px', height: '30px', background: '#333' }}></div>

            {/* 5. Complexity Matrix (4-Vector) */}
            {/* Quarter-Width Layout: width 100%, flex 1 for children */}
            <div style={{ display: 'flex', gap: '5px', width: '300px' }}>
                {/* Rhythmic Density */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <span style={{ fontSize: '0.6rem', color: '#666', marginBottom: '2px' }}>DENSITY</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={rhythmicDensity}
                        onChange={(e) => setRhythmicDensity(parseInt(e.target.value))}
                        title="Rhythmic Density"
                        style={{ width: '100%', cursor: 'pointer' }}
                    />
                </div>
                {/* Harmonic Freedom */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <span style={{ fontSize: '0.6rem', color: '#666', marginBottom: '2px' }}>HARMONY</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={harmonicFreedom}
                        onChange={(e) => setHarmonicFreedom(parseInt(e.target.value))}
                        title="Harmonic Freedom"
                        style={{ width: '100%', cursor: 'pointer' }}
                    />
                </div>
                {/* Melodic Range */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <span style={{ fontSize: '0.6rem', color: '#666', marginBottom: '2px' }}>RANGE</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={melodicRange}
                        onChange={(e) => setMelodicRange(parseInt(e.target.value))}
                        title="Melodic Range"
                        style={{ width: '100%', cursor: 'pointer' }}
                    />
                </div>
                {/* Timbral Motion */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <span style={{ fontSize: '0.6rem', color: '#666', marginBottom: '2px' }}>MOTION</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={timbralMotion}
                        onChange={(e) => setTimbralMotion(parseInt(e.target.value))}
                        title="Timbral Motion"
                        style={{ width: '100%', cursor: 'pointer' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default GlobalControlBar;
